/**
 * Variant weight rebalance algorithm (#249).
 *
 * Given an array of variant weights whose sum may not equal 100, return
 * a new array whose sum is exactly 100, distributing the delta equally
 * across all variants. The first variant absorbs the remainder so the
 * result lands on exactly 100. No resulting weight may drop below 1 — a
 * zero-weight variant would never be picked by the assignment algorithm,
 * so we preserve operator intent that the variant exists. If a clamping
 * pass cannot reach 100 (e.g. all variants are already at 1 and the sum
 * is still below 100, or all are at large values with no slack), return
 * `null` so the caller can surface a "can't auto-fix" toast and skip the
 * PATCH round-trip.
 *
 * Algorithm:
 *   1. delta = 100 - sum
 *   2. perVariantDelta = Math.trunc(delta / N)
 *   3. firstDelta = delta - perVariantDelta * (N - 1) — the first variant
 *      absorbs the remainder so the sum lands exactly on 100.
 *   4. Apply. Any output below 1 is clamped to 1 and the shortfall is
 *      redistributed across the remaining unclamped variants. Repeat
 *      until stable (worst case N passes). If the final sum still isn't
 *      100 (or any value is still below 1), return null.
 *
 * Pure function — no side effects, easy to unit-test in isolation.
 */

export function rebalanceWeights(weights: number[]): number[] | null {
  const N = weights.length;
  if (N === 0) return null;
  // Any non-finite or below-1 input is operator-invalid — bail rather
  // than try to "fix" a degenerate input. The numeric input control in
  // the detail page enforces min=1 already.
  for (const w of weights) {
    if (!Number.isFinite(w) || w < 1) return null;
  }

  const sum = weights.reduce((a, b) => a + b, 0);
  if (sum === 100) return [...weights];

  // Iteratively distribute the delta across unclamped indices. Lock any
  // index that would land below 1 at value 1 and retry. Worst-case
  // termination is O(N) passes (each pass either locks at least one
  // index or settles).
  const out = [...weights];
  const clamped = new Array<boolean>(N).fill(false);

  for (let iter = 0; iter <= N; iter++) {
    // Reset unclamped slots to their original input before recomputing
    // the delta — so each pass works against the original weights.
    for (let i = 0; i < N; i++) {
      if (!clamped[i]) out[i] = weights[i]!;
    }

    const unclampedIdxs: number[] = [];
    let clampedSum = 0;
    for (let i = 0; i < N; i++) {
      if (clamped[i]) clampedSum += 1;
      else unclampedIdxs.push(i);
    }
    if (unclampedIdxs.length === 0) {
      // Every index is locked at 1, but the sum still doesn't hit 100.
      return null;
    }

    const unclampedOriginalSum = unclampedIdxs.reduce((acc, i) => acc + weights[i]!, 0);
    const remaining = 100 - clampedSum;
    const delta = remaining - unclampedOriginalSum;
    const M = unclampedIdxs.length;
    const perDelta = Math.trunc(delta / M);
    // The first unclamped variant absorbs the remainder. Mirrors the
    // spec: `firstDelta = delta - perVariantDelta * (N - 1)`.
    const firstDelta = delta - perDelta * (M - 1);

    for (let k = 0; k < M; k++) {
      const i = unclampedIdxs[k]!;
      out[i] = weights[i]! + (k === 0 ? firstDelta : perDelta);
    }

    let newClamp = false;
    for (const i of unclampedIdxs) {
      if (out[i]! < 1) {
        clamped[i] = true;
        out[i] = 1;
        newClamp = true;
      }
    }
    if (!newClamp) break;
  }

  // Final sanity — if anything is still below 1 or the total isn't 100,
  // surface the degenerate case to the caller.
  if (out.some((w) => w < 1)) return null;
  const finalSum = out.reduce((a, b) => a + b, 0);
  if (finalSum !== 100) return null;
  return out;
}
